import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { uid } from "quasar";
import { compact, last } from "lodash";
import { deepFreeze, formatCurrency } from "../util/misc";
import { highlightDescription } from "./SaleItem";

export const QUOTE_MATERIAL_ITEM_FIELDS = {
  id: "",
  inventoryID: null,
  itemType: "material",
  line: 0,
  category: "",
  description: "",
  image: null,
  brand: "",
  series: "",
  location: "",
  quantity: 1,
  margin: null,
  cost: null,
  retail: null,
  price: null,
  notax: false,
};
deepFreeze(QUOTE_MATERIAL_ITEM_FIELDS);

export default class QuoteMaterialItem extends DataStructure {
  constructor(data = {}, inventoryID) {
    if (inventoryID && !data.inventoryID) {
      data.inventoryID = inventoryID;
      let item = store.state.main.inventoryItems[inventoryID];
      if (item) {
        data.image = item.data.images[0]
          ? {
              ...item.data.images[0],
              src: item.getThumbnail(),
              ext: last(item.data.images[0].id.split(".")),
            }
          : null;
        data.brand = item.data.brand;
        data.series = item.data.series;
        data.description = item.data.description;
        data.quantity = item.data.quantity;
        data.cost = item.data.cost;
        data.retail = item.data.retail;
        if (data.image) {
          fetch(data.image.src)
            .then((r) => r.blob())
            .then(
              (blob) =>
                new Promise((resolve) => {
                  let reader = new FileReader();
                  reader.onload = () => resolve(reader.result);
                  reader.readAsDataURL(blob);
                })
            )
            .then((dataURL) => (this.data.image.src = dataURL));
        }
      }
    }

    data = sanitize(data, QUOTE_MATERIAL_ITEM_FIELDS);

    if (!data.id) {
      data.id = uid().slice(-12);
    }

    super(data);
  }

  // Editing

  get isValid() {
    return Boolean(
      this.data.description &&
        this.data.price !== null &&
        this.data.cost !== null &&
        this.data.quantity >= 1 &&
        // A sale item is found by brand everywhere downstream (ordering,
        // receiving, QC all filter on brand === vendor), so an item sold
        // without one silently drops out of all of them. Inventory items are
        // already in the warehouse and are sold without a brand.
        (this.data.inventoryID || (this.data.brand && this.data.series))
    );
  }

  // Formatting
  get inventoryItem() {
    return this.data.inventoryID
      ? store.state.main.inventoryItems[this.data.inventoryID]
      : null;
  }

  get cost() {
    return this.data.cost === null ? null : formatCurrency(this.data.cost);
  }

  get retail() {
    return this.data.retail === null ? null : formatCurrency(this.data.retail);
  }

  get price() {
    return this.data.price === null ? null : formatCurrency(this.data.price);
  }

  get total() {
    return formatCurrency(
      this.data.price === null ? 0 : this.data.quantity * this.data.price
    );
  }

  get description() {
    return highlightDescription(this.data.description);
  }

  summary(length = 200) {
    const description = this.data.description.replace(
      /^\*\*+[^*]+\*\*+\s*/gm,
      ""
    );
    return length === null
      ? description
      : description.slice(0, length) + (description.length > length ? "…" : "");
  }

  toString() {
    return compact([
      this.data.brand,
      this.data.series,
      this.data.location,
      this.summary(),
    ]).join("\n");
  }
}
